import { isDefined } from "@clipboard-health/util-ts";
import { showErrorToast } from "@src/appV2/lib/Notifications";
import { type KeyboardEvent, useCallback, useState } from "react";

import { type SearchResultItem } from "./useOmnisearchResults";

type CopiedItemLabel = "worker" | "workplace" | "shift" | "stripe" | "agent" | "facility";

function getIsMac(): boolean {
  return navigator.platform.toUpperCase().includes("MAC");
}

function copyToClipboard(
  text: string,
  label: CopiedItemLabel,
  setCopiedItem: (item: CopiedItemLabel | null) => void
): boolean {
  if (!isDefined(navigator.clipboard)) {
    showErrorToast("Unable to copy to clipboard");
    return false;
  }

  navigator.clipboard.writeText(text).then(
    () => {
      setCopiedItem(label);
      setTimeout(() => {
        setCopiedItem(null);
      }, 2000);
    },
    () => {
      showErrorToast("Failed to copy to clipboard");
    }
  );

  return true;
}

interface UseOmnisearchKeyboardParams {
  allResults: SearchResultItem[];
  selectedIndex: number;
  setSelectedIndex: (index: number | ((previous: number) => number)) => void;
  setExpandedShiftId: (id: string | null) => void;
  setExpandedWorkerId: (id: string | null) => void;
  setExpandedWorkplaceId: (id: string | null) => void;
  onSelect: (item: SearchResultItem) => void;
}

export function useOmnisearchKeyboard(params: UseOmnisearchKeyboardParams) {
  const {
    allResults,
    selectedIndex,
    setSelectedIndex,
    setExpandedShiftId,
    setExpandedWorkerId,
    setExpandedWorkplaceId,
    onSelect,
  } = params;
  const [copiedItem, setCopiedItem] = useState<CopiedItemLabel | null>(null);

  const handleCopySelectedId = useCallback(() => {
    const selectedItem = allResults[selectedIndex];

    if (selectedItem?.type === "agent") {
      return copyToClipboard(selectedItem.data.userId, "agent", setCopiedItem);
    }

    if (selectedItem?.type === "facility") {
      return copyToClipboard(selectedItem.data.userId, "facility", setCopiedItem);
    }

    if (selectedItem?.type === "agent-shift" || selectedItem?.type === "workplace-shift") {
      return copyToClipboard(selectedItem.data.id, "shift", setCopiedItem);
    }

    if (selectedItem?.type === "agent-stripe") {
      return copyToClipboard(selectedItem.data.accountId, "stripe", setCopiedItem);
    }

    if (selectedItem?.type !== "shift-action") {
      return false;
    }

    const id =
      selectedItem.action === "worker"
        ? selectedItem.data.agent?.userId
        : selectedItem.data.facility?.userId;

    if (!isDefined(id)) {
      return false;
    }

    return copyToClipboard(id, selectedItem.action, setCopiedItem);
  }, [allResults, selectedIndex]);

  const handleKeyDown = useCallback(
    // eslint-disable-next-line complexity -- keyboard handler requires multiple branches for different key combinations
    (event: KeyboardEvent) => {
      const selectedItem = allResults[selectedIndex];
      const isModifierKey = getIsMac() ? event.metaKey : event.ctrlKey;

      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((previous) => Math.min(previous + 1, Math.max(allResults.length - 1, 0)));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((previous) => Math.max(previous - 1, 0));
      } else if (
        (event.key === "ArrowRight" || event.key === "Tab") &&
        selectedItem?.type === "shift"
      ) {
        event.preventDefault();
        setExpandedShiftId(selectedItem.data.id);
      } else if (
        (event.key === "ArrowRight" || event.key === "Tab") &&
        selectedItem?.type === "agent" &&
        !selectedItem.expanded
      ) {
        event.preventDefault();
        setExpandedWorkerId(selectedItem.data.userId);
      } else if (
        (event.key === "ArrowRight" || event.key === "Tab") &&
        selectedItem?.type === "facility" &&
        !selectedItem.expanded
      ) {
        event.preventDefault();
        setExpandedWorkplaceId(selectedItem.data.userId);
      } else if (
        event.key === "ArrowLeft" &&
        (selectedItem?.type === "shift-action" ||
          (selectedItem?.type === "shift" && selectedItem.expanded))
      ) {
        event.preventDefault();
        setExpandedShiftId(null);
      } else if (
        event.key === "ArrowLeft" &&
        (selectedItem?.type === "agent-shift" ||
          selectedItem?.type === "agent-stripe" ||
          selectedItem?.type === "agent-impersonate" ||
          (selectedItem?.type === "agent" && selectedItem.expanded))
      ) {
        event.preventDefault();
        setExpandedWorkerId(null);
      } else if (
        event.key === "ArrowLeft" &&
        (selectedItem?.type === "workplace-shift" ||
          (selectedItem?.type === "facility" && selectedItem.expanded))
      ) {
        event.preventDefault();
        setExpandedWorkplaceId(null);
      } else if (event.key === "Enter" && allResults.length > 0) {
        event.preventDefault();
        if (isDefined(selectedItem)) {
          onSelect(selectedItem);
        }
      } else if (
        isModifierKey &&
        event.key === "c" &&
        (selectedItem?.type === "agent" ||
          selectedItem?.type === "facility" ||
          selectedItem?.type === "shift-action" ||
          selectedItem?.type === "agent-shift" ||
          selectedItem?.type === "agent-stripe" ||
          selectedItem?.type === "workplace-shift")
      ) {
        event.preventDefault();
        handleCopySelectedId();
      }
    },
    [
      allResults,
      selectedIndex,
      setSelectedIndex,
      setExpandedShiftId,
      setExpandedWorkerId,
      setExpandedWorkplaceId,
      onSelect,
      handleCopySelectedId,
    ]
  );

  return {
    handleKeyDown,
    handleCopySelectedId,
    copiedItem,
  };
}
